// ============================================================
// Alpine: shopCheckout — alamat, ongkir, pembayaran
// ============================================================

import Alpine from 'alpinejs';
import axios from '../../config/axios.js';
import { requireAuth, shopApi } from '../../helpers/shopAuth.js';
import { createUserAddressForm } from '../../helpers/userAddressForm.js';

export function registerShopCheckout() {
  Alpine.data('shopCheckout', () => ({
    addresses: [],
    addressId: '',
    shippingMethod: 'reguler',
    paymentMethod: 'duitku',
    notes: '',
    voucherCode: '',
    appliedVoucher: null,
    voucherBusy: false,
    preview: null,
    loading: true,
    submitting: false,
    shippingOptions: [],
    galleryOpen: false,
    galleryProduct: null,
    galleryItems: [],
    galleryActive: 0,
    error: '',

    paymentOptions: [
      { value: 'duitku', label: 'Bayar Online (Duitku)' },
      { value: 'transfer_manual', label: 'Transfer Bank' },
    ],

    ...createUserAddressForm({
      async onSaved(address) {
        await this.loadAddresses();
        if (address?.id) {
          this.addressId = address.id;
          await this.loadPreview();
        }
      },
    }),

    async init() {
      if (!requireAuth('/shop/checkout')) return;
      await this.initAddressForm();
      await this.loadAddresses();
    },

    formatRp(n) {
      return window.formatRupiah ? window.formatRupiah(n) : `Rp ${Number(n).toLocaleString('id-ID')}`;
    },

    selectedShippingLabel() {
      const opt = this.shippingOptions.find((o) => o.value === this.shippingMethod);
      return opt?.label || this.shippingMethod;
    },

    checkoutThumb(item) {
      const gallery = item.product?.gallery || [];
      const firstPhoto = gallery.find((g) => g.type === 'photo') || gallery[0];
      if (firstPhoto?.thumbnail_url || firstPhoto?.url) {
        return firstPhoto.thumbnail_url || firstPhoto.url;
      }
      if (item.product?.image) {
        return item.product.image;
      }
      return '/images/placeholder-product.png';
    },

    openGallery(item) {
      const gallery = item.product?.gallery || [];
      if (!gallery.length) return;
      this.galleryProduct = item.product;
      this.galleryItems = gallery;
      this.galleryActive = 0;
      this.galleryOpen = true;
    },

    applyShippingOptions(options = []) {
      this.shippingOptions = options.map((opt) => ({
        value: opt.key,
        label: `${opt.label} — ${opt.formatted_price}`,
        eta: opt.eta,
        is_free: opt.is_free,
        free_reason: opt.free_reason,
        price: opt.price,
      }));

      if (this.shippingOptions.length && !this.shippingOptions.some((o) => o.value === this.shippingMethod)) {
        this.shippingMethod = this.shippingOptions[0].value;
      }
    },

    async loadAddresses() {
      this.loading = true;
      try {
        const { data } = await axios.get(shopApi('/profile/addresses'));
        this.addresses = data.data || [];
        const def = this.addresses.find((a) => a.is_default) || this.addresses[0];
        this.addressId = def?.id || '';

        if (!this.addresses.length) {
          this.openAddressForm();
          this.shippingMethod = 'pickup';
        }

        if (this.addressId || this.shippingMethod === 'pickup') {
          await this.loadPreview();
        }
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal memuat alamat.';
      } finally {
        this.loading = false;
      }
    },

    async loadPreview() {
      if (!this.addressId && this.shippingMethod !== 'pickup') {
        this.preview = null;
        return;
      }

      try {
        const payload = {
          shipping_method: this.shippingMethod,
        };
        if (this.shippingMethod !== 'pickup') {
          payload.address_id = this.addressId;
        }
        const { data } = await axios.post(shopApi('/checkout/preview'), payload);
        this.preview = data.data;
        this.applyShippingOptions(data.data?.shipping_options || []);
        this.error = '';
      } catch (e) {
        this.preview = null;
        this.error = e.response?.data?.message || 'Gagal menghitung total.';
      }
    },

    async applyVoucher() {
      const code = this.voucherCode.trim();
      if (!code) return;
      this.voucherBusy = true;
      try {
        const { data } = await axios.post(shopApi('/cart/apply-voucher'), { voucher_code: code });
        this.appliedVoucher = data.data?.voucher || { code: code.toUpperCase() };
        this.voucherCode = '';
        window.toast?.(data.message || 'Kode promo diterapkan.', 'success');
        await this.loadPreview();
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Kode promo tidak valid.', 'error');
      } finally {
        this.voucherBusy = false;
      }
    },

    async removeVoucher() {
      this.voucherBusy = true;
      try {
        await axios.delete(shopApi('/cart/voucher'));
        this.appliedVoucher = null;
        window.toast?.('Kode promo dilepas.', 'success');
        await this.loadPreview();
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal melepas kode.', 'error');
      } finally {
        this.voucherBusy = false;
      }
    },

    async submitOrder() {
      if (this.shippingMethod !== 'pickup') {
        if (!this.addressId) {
          this.error = 'Pilih atau tambahkan alamat pengiriman.';
          this.openAddressForm();
          return;
        }
        const owned = this.addresses.some((a) => a.id === this.addressId);
        if (!owned) {
          this.error = 'Alamat pengiriman tidak valid. Pilih alamat Anda.';
          const def = this.addresses.find((a) => a.is_default) || this.addresses[0];
          this.addressId = def?.id || '';
          if (this.addressId) {
            await this.loadPreview();
          }
          return;
        }
      }

      this.submitting = true;
      this.error = '';

      try {
        const body = {
          shipping_method: this.shippingMethod,
          payment_method: this.paymentMethod,
          notes: this.notes || null,
        };
        if (this.shippingMethod !== 'pickup') {
          body.address_id = this.addressId;
        }
        const { data } = await axios.post(shopApi('/checkout'), body);

        const payload = data.data;
        sessionStorage.setItem('uspatih_pending_order', JSON.stringify(payload));
        Alpine.store('cart').clear();
        window.location.href = `/shop/payment/${payload.order_id}`;
      } catch (e) {
        this.error = e.response?.data?.message || 'Checkout gagal. Coba lagi.';
      } finally {
        this.submitting = false;
      }
    },
  }));
}
